import { ArticleBlock, paragraphs } from '../types/article'

const IMG_APP_SETUP =
  'https://cdn.magicpatterns.com/patterns/generated-images/93171f36-f225-48d9-9749-f21e6b4ba9a2.jpg'
const IMG_BOUNDARY =
  'https://cdn.magicpatterns.com/patterns/generated-images/7d4fa89d-9f0c-44be-9e1f-0800c0b1cf62.jpg'

export const storyPartTwo: ArticleBlock[] = [
  {
    kind: 'heading',
    text: `I Found It at Midnight. In Bed. Scrolling My Phone. Trying Not to Cry.`,
  },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/uploads/hER4fxuMFudxQ44eQ7UwTY/hf_20260826_111150_f3e4692c-a82e-4301-8512-159d3b65754c.webp',
    alt: 'A woman lying in a dark bedroom at night, face lit by her phone as she searches for a GPS dog fence',
    caption:
      'Midnight, after another escape. Searching the same thing for the tenth time — and this time something new came up.',
  },
  ...paragraphs(
    `I'm not going to pretend I found this through some brilliant research.`,
    `I found it because I was lying in bed at midnight after Bear got out again, scrolling my phone, trying not to cry.`,
    `I was Googling [[GPS dog fence no subscription]] for probably the tenth time.`,
    `And this time, something different came up.`,
    `It was a GPS fence collar I had never heard of.`,
    `No buried wires.`,
    `No professional installation.`,
    `No monthly subscription.`,
    `Just a collar with a GPS unit built in that creates an invisible boundary around your yard.`,
    `You download a free app, walk your boundary line, and the boundary is set.`,
    `No subscription to activate it.`,
    `No monthly fee to keep using it.`,
    `The app is free.`,
    `And it **stays** free.`,
  ),
  {
    kind: 'image',
    src: IMG_APP_SETUP,
    alt: 'Hands holding a phone in a backyard showing a drawn boundary line around the yard',
    caption:
      'You walk the line you want, confirm it in the free app, and that is your fence. Mine took about ten minutes.',
  },
  ...paragraphs(
    `Now — I almost scrolled right past it.`,
    `Because I'd already read all those reviews about cheap GPS collars that drifted 20 feet.`,
    `Where the boundary was so loose that a dog could be across the street before the collar even knew it had crossed the line.`,
    `That's not a fence.`,
    `That's a suggestion.`,
    `So I kept reading.`,
    `And that's where I found something that made me stop scrolling.`,
    `The cheap GPS collars that people complained about use basic GPS chips — the same kind you'd find in a fitness tracker or a car phone mount.`,
    `Those chips connect to a handful of satellites.`,
    `And a handful of satellites can tell you **roughly** where something is.`,
    `But roughly is not good enough when your dog's life depends on knowing exactly where the boundary is.`,
    `This collar was different.`,
    `It uses newer GPS chips that connect to multiple satellite networks at the same time — not just one.`,
    `The same kind of GPS system that planes use to land.`,
    `Instead of a few satellites guessing where your dog is, it uses a lot more to know exactly.`,
    `That's why the boundary holds tight instead of drifting.`,
    `And it never stops checking — so the second your dog moves toward the edge, the collar catches it.`,
    `First, a beep.`,
    `If your dog keeps going, a vibration.`,
    `If your dog still keeps going — and most dogs stop at the beep — a mild static correction.`,
    `Not a punishment.`,
    `A warning system.`,
    `Like rumble strips on a highway.`,
    `You drift toward the shoulder, you feel the vibration, you correct, you don't crash.`,
    `Your dog approaches the boundary, it hears the beep, it turns around, it doesn't escape.`,
    `Most dogs learn the boundary within days — and never need anything beyond the beep.`,
    `When I read that, something clicked.`,
    `This wasn't just another cheap GPS collar with a different name.`,
    `The technology was actually different.`,
    `The training approach was actually different.`,
    `And it came with a **60-day money-back guarantee** — a real one.`,
    `No fine print.`,
    `No partial refund.`,
    `No "you opened the box so we won't take it back."`,
    `Full refund if it didn't keep my dog in the yard.`,
    `The risk was almost nothing.`,
    `And if it worked, I could finally stop worrying about losing Bear.`,
    `So I ordered it.`,
  ),
  {
    kind: 'heading',
    text: `The Box Showed Up Ten Days Later. I Opened It on My Kitchen Table.`,
  },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/uploads/qXUFjbP51PQSj46GbUaZYW/hf_20260826_111555_1f3adfc5-ffbd-45c6-bb28-3164a7e1804c.webp',
    alt: 'Overhead view of the GPS fence collar in its opened box on a kitchen table, with a coffee mug and a husky sniffing the packaging',
    caption:
      'Bear checked the box before I did. Collar, strap, charging cable — that was the whole kit.',
  },
  ...paragraphs(
    `After I ordered it, I had about ten days of waiting.`,
    `And every one of those days, I was checking the front door for the box and watching Bear in the yard like I always do.`,
    `Hands tight.`,
    `Eyes on the fence line.`,
    `Same knot in my stomach I'd had for two years.`,
    `When the box finally showed up, I brought it inside and opened it on the kitchen table.`,
    `The collar itself was smaller than I expected.`,
    `A small black GPS unit on a black nylon strap with some reflective stitching.`,
    `It didn't look like a piece of junk from some random warehouse.`,
    `It looked like something someone actually thought about.`,
  ),
  ...paragraphs(
    `There was a small LCD screen on the GPS unit and a few buttons.`,
    `I downloaded the app — free, no account fee, no subscription — and it walked me through the setup step by step.`,
    `Charge the collar.`,
    `Walk your boundary with it.`,
    `Confirm in the app.`,
    `Done.`,
    `The whole thing took about ten minutes.`,
    `I stood in my yard looking at my phone, seeing the boundary I'd just drawn on the screen, thinking there is no way it was that easy.`,
    `Only one way to find out.`,
  ),
  {
    kind: 'heading',
    text: `He Walked Toward the Fence Line. The Collar Beeped Once. He Turned Around.`,
  },
  {
    kind: 'video',
    src: 'https://cdn.magicpatterns.com/uploads/4wjiRa8BCN39bzbjd2F72S/hf_20260826_150415_11c35c6e-6125-4a5f-859f-334f9d8aa3da.mp4',
    caption:
      'The moment he hit the boundary. A beep, a pause, and he turned right back toward the house.',
  },
  ...paragraphs(
    `I'm going to be honest with you.`,
    `I was nervous.`,
    `Not because I was afraid it wouldn't work.`,
    `Because I was afraid of what would happen to Bear if it did.`,
    `I'd read all the things people say about shock collars.`,
    `That they're cruel.`,
    `That they traumatize dogs.`,
    `That they break the bond between you and your dog.`,
    `And I'd already read the story about the woman whose dog became so afraid of the yard after wearing an invisible fence collar that the dog wouldn't go outside at all.`,
    `So when I put the collar on Bear and let him out into the yard, my stomach was in a knot.`,
    `He did what he always does.`,
    `Trotted around, sniffed along the fence line, and started heading toward the spot where he usually pushes through.`,
    `Then the collar beeped.`,
    `Just a beep.`,
    `He stopped.`,
    `His ears went up.`,
    `He looked around.`,
    `Then he turned and walked back toward the middle of the yard.`,
  ),
  ...paragraphs(
    `A husky.`,
    `Turning around.`,
    `Without me doing anything.`,
    `That was it.`,
    `No shock.`,
    `No yelp.`,
    `No fear.`,
    `Just a beep, and he turned around.`,
    `I stood on the porch and watched him do it two more times that afternoon.`,
    `Beep.`,
    `Turn.`,
    `Walk back.`,
    `By the third day, he wasn't even going near the boundary line anymore.`,
    `Same beep, same spot, every time — and he just figured out where the edge was and stopped going there.`,
    `The static correction — the actual "shock" part that I was so afraid of?`,
    `He never needed it.`,
    `The beep taught him.`,
    `That was the moment I started breathing again.`,
  ),
  {
    kind: 'heading',
    text: `Two Weeks Later I Was Sitting on My Porch With Coffee. Not Watching the Gate.`,
  },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/uploads/4EY7BPBPnxGzq8vn24hWra/hf_20260826_120208_59c9790e-c6f5-4a48-a540-b0236870bec8.webp',
    alt: 'A woman relaxing in a porch chair with coffee and a book while her husky trots freely across the sunlit yard',
    caption:
      'Two weeks in. Coffee in one hand, a book in the other, and Bear running the yard without me watching the gate.',
  },
  ...paragraphs(
    `I want to try to explain this part because it's the part that matters most.`,
    `For almost two years, every time Bear was in the yard, I was watching.`,
    `Standing at the window.`,
    `Sitting on the porch with one hand on the door handle.`,
    `Checking the gate.`,
    `Listening for the sound of him pushing through the fence.`,
    `I could never just sit there.`,
    `Two weeks after I set up the collar, I was sitting on my porch with a cup of coffee.`,
    `Bear was running the yard.`,
    `Chasing a bird.`,
    `Rolling in the grass.`,
    `Being a dog.`,
    `And I was just sitting there.`,
  ),
  ...paragraphs(
    `Not watching the gate.`,
    `Not listening for the fence.`,
    `Not ready to sprint after him.`,
    `Just sitting there.`,
    `My husband came outside and sat on the steps.`,
    `He watched Bear run the yard for about five minutes.`,
    `Then he said, "I think we can take the fence fund off the budget."`,
    `That was the first time he'd ever admitted it was solved.`,
    `I think I actually said the word "finally" out loud to nobody.`,
  ),
  {
    kind: 'heading',
    text: `My Dog Has Worn This Collar for Months. He Has Never Been Shocked. Not Once.`,
  },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/uploads/wEoULT7WZFsguB7qTbzvym/hf_20260826_115259_ccf1d6db-04a1-4bd8-934b-a9599b0bad4c.webp',
    alt: 'A relaxed husky lying in sunlit grass wearing the black Rylte GPS fence collar',
    caption:
      'Months in the collar and not one static correction. He learned it from the beep in three days.',
  },
  ...paragraphs(
    `Now — I know there's probably one thing you're still wondering about.`,
    `Because it was the one thing that almost stopped me too.`,
    `The shock.`,
    `Here's the truth.`,
    `Bear has worn this collar for months.`,
    `He has never been shocked.`,
    `Not once.`,
    `He learned from the beep alone.`,
    `It took him three days.`,
    `**Three days** to learn a boundary that two years of my training couldn't teach him.`,
    `And now he doesn't even react to the beep anymore — because he never goes close enough to hear it.`,
    `I was so afraid this collar would hurt him or scare him or break the trust between us.`,
    `It didn't do any of those things.`,
    `It just taught him where the edge was.`,
    `And he respected it.`,
    `That's when I stopped calling it a shock collar in my head and started calling it what it actually is.`,
    `A training tool.`,
  ),
  {
    kind: 'heading',
    text: `The Fence Quote Was $6,200. The Real Solution Cost Less Than a Single Vet Visit.`,
  },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/uploads/gJRsDxZ7yvwNuzRmLgLRrZ/hf_20260826_112411_0b1b0809-f878-44ae-a19e-aefd577c41a4.webp',
    alt: 'A crumpled fencing company estimate for $6,200 crossed out in red marker, next to the GPS fence collar on a kitchen table',
    caption:
      'The quote I kept on the counter for months, next to what actually solved it.',
  },
  ...paragraphs(
    `So it works.`,
    `It's humane.`,
    `And the technology behind it is real.`,
    `The next question I had — and I bet you have it too — was about the money.`,
    `The fence company quoted me over $6,000.`,
    `Halo would have cost me over $600 plus a monthly fee for as long as I owned Bear.`,
    `A buried-wire invisible fence was thousands installed.`,
    `This collar cost me **less than a single vet visit**.`,
    `No subscription.`,
    `Not now, not later, not ever.`,
    `No installation.`,
    `No contractor.`,
    `No monthly fees.`,
    `The app is free — no subscription, no hidden charges, nothing.`,
    `And it came with a 60-day money-back guarantee.`,
    `Not a partial refund.`,
    `Not a "sorry you opened the box" refund.`,
    `A real, full refund if it didn't keep my dog in the yard.`,
    `Now — I know what you might be thinking.`,
    `Why is this so much cheaper than Halo or SpotOn?`,
    `I wondered the same thing.`,
    `Here's what I figured out.`,
    `Those collars are trying to do everything — GPS tracking, activity monitoring, health features, cellular service, monthly subscriptions.`,
    `This collar does one thing.`,
    `It keeps your dog in your yard.`,
    `That's it.`,
    `You're not paying for a gadget that tries to do ten things.`,
    `You're paying for a fence that does one thing well.`,
    `And it turns out, when a company focuses on doing one thing right instead of ten things at once, it costs a lot less.`,
    `I spent less on this collar than I spent on gas driving around my neighborhood looking for Bear.`,
  ),
  { kind: 'heading', text: `It's Called the Rylte GPS Fence Collar.` },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/uploads/4iJ8pSNqVDVpuaxMPT14bS/hf_20260826_113139_c61fa4ad-fe42-44d9-bb26-c4efe68a9984.webp',
    alt: 'The Rylte GPS fence collar: a black reflective strap with a compact GPS module showing a small status screen',
    caption:
      'The Rylte GPS Fence Collar. One job — keeping your dog inside the boundary you walked.',
  },
  ...paragraphs(
    `The collar is the [[Rylte GPS Fence Collar]].`,
    `I don't work for them.`,
    `I'm not getting paid to tell you about them.`,
    `I'm telling you because I spent two years in the same panic you're in right now, and I wish someone had told me about this sooner.`,
    `Here's what you get:`,
  ),
  {
    kind: 'callout',
    title: `What comes with the Rylte GPS Fence Collar`,
    items: [
      `A GPS fence collar that puts an invisible boundary around your yard — no matter how big it is or what shape. No buried wires. No base station. No professional installation. No monthly subscription.`,
      `A free app that walks you through setup and shows your boundary on your phone — no subscription fee, no monthly charge, ever.`,
      `A beep, then a vibration, then a small correction only if the dog ignores both — most dogs never get past the beep.`,
      `Better GPS that actually holds the boundary tight — not the cheap kind that drifts 20 feet.`,
      `A rechargeable battery with a low-battery alert so you're never caught off guard.`,
      `And a 60-day money-back guarantee with no catch. If it doesn't keep your dog in your yard, you send it back and get every penny refunded. No questions. No games. No "you opened the packaging" technicalities.`,
    ],
  },
  ...paragraphs(
    `That's what I paid to stop being afraid every time Bear went into the yard.`,
  ),
  {
    kind: 'cta',
    label: `See the Rylte GPS Fence Collar and Check the Price in Your Country`,
  },
  { kind: 'reviews' },
  {
    kind: 'heading',
    text: `Every Day You Wait Is Another Day Your Dog Could Get Out and Not Come Back.`,
  },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/uploads/pGnB3ZmLnUNFKqMW5kiBbL/hf_20260826_113153_471173c4-6d39-44bb-885e-b7ee7e31693f.webp',
    alt: 'An open backyard gate at dusk with a leash still hanging on the post, looking out to a quiet suburban street',
    caption:
      'Every gate left open is a clock you cannot see. The only question is how many times your luck holds.',
  },
  ...paragraphs(
    `If you have a dog that escapes, you already know how this story ends if you don't do something.`,
    `You've imagined it.`,
    `You've probably imagined it more times than you want to admit.`,
    `And you've probably told yourself that you'll figure it out eventually.`,
    `That you'll build a fence when you can afford it.`,
    `That you'll train him better.`,
    `That he'll grow out of it.`,
    `He won't.`,
    `Dogs that escape don't stop escaping.`,
    `They get better at it.`,
    `And every time your dog gets out and comes back, it's not proof that everything is fine.`,
    `It's luck.`,
    `And luck runs out.`,
    `I'm not telling you this to scare you.`,
    `I'm telling you because I told myself the same things for two years.`,
    `And the night I was standing in my driveway, barefoot, calling Bear's name into the dark — that was the night I realized I'd been gambling with his life every single day I waited.`,
  ),
  {
    kind: 'heading',
    text: `You're Not a Bad Dog Owner. You Just Never Had a Real Option. Now You Do.`,
  },
  {
    kind: 'image',
    src: 'https://cdn.magicpatterns.com/uploads/cYFktPkUnLue4AEe8LgDbY/hf_20260826_153054_8c55a42e-8591-4dba-b650-4c2a1a24d92e.png',
    alt: 'A couple sitting happily on their porch steps while their husky wearing a GPS collar runs freely in the sunlit yard',
    caption:
      'Your dog deserves to run free in your yard without being in danger. And you deserve to finally stop worrying.',
  },
  ...paragraphs(
    `I want to say this because I needed to hear it, and nobody said it to me.`,
    `You are not a bad dog owner because your dog escapes.`,
    `You are not a bad neighbor because your dog ended up in someone else's yard.`,
    `You are not a bad person because you can't afford thousands for a fence.`,
    `Your dog was built to run.`,
    `And until now, there was nothing out there that actually worked, didn't cost a fortune, and didn't hurt your dog.`,
    `Now there is.`,
    `If this sounds like something that could help your dog, here's the link.`,
  ),
  {
    kind: 'cta',
    label: `See If the Rylte GPS Fence Collar Is Right for Your Dog`,
  },
  ...paragraphs(
    `Your dog deserves to run free in your yard without being in danger.`,
    `And you deserve to finally stop worrying.`,
  ),
]
